// components/CustomerMapModal/index.tsx
// 顧客地図モーダル（周辺顧客を地図上で確認する）
//
// ■ 目的
//   ユーザーが「対象顧客の周辺にいる顧客情報」を地図上で確認する。
//   PC版(ref/map)の 左:地図 / 右:一覧 レイアウトをスマホ向けに再設計したもの。
//
// ■ レイアウト（ボトムシート型）
//   ・地図を全面に表示（Google Maps）
//   ・ヘッダーに検索ボタン / マップで開くボタン / 閉じる
//   ・下部に「引き上げ式のシート」で周辺顧客リスト（ハンドルのタップで高さ切替）
//
// ■ 接続
//   データ・状態・検索は useCustomerMap（参考 customermap.js の移植）、
//   地図描画・ピン・吹き出しは MapCanvas が担う。

import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Select, Tag, Pagination, Typography, Spin, Empty, Space } from 'antd';
import {
  SearchOutlined,
  EnvironmentFilled,
  EnvironmentOutlined,
  UpOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  PhoneOutlined,
  MobileOutlined,
  SelectOutlined,
} from '@ant-design/icons';
import { MODAL_Z } from '../../utils/modalZIndex';
import { getRankColorByMasterOrder } from '../../services/rank-utils';
import { dialPhone } from '../../utils/linkify';
import { useCustomerMap } from '../../hooks/useCustomerMap';
import MapCanvas from './MapCanvas';
import './CustomerMapModal.css';

/** useCustomerMap の戻り値型。地図状態は呼び出し元（ページ）で保持し、prop で受け取る。 */
export type CustomerMapController = ReturnType<typeof useCustomerMap>;

const { Text } = Typography;
const CONST_NOT_SELECTED = -1; // クエリー未選択値

// ----------------------------------------------------------------
// Props
// ----------------------------------------------------------------
export interface CustomerMapModalProps {
  /** モーダル表示状態 */
  open: boolean;
  /** 閉じる */
  onClose: () => void;
  /** 対象顧客の住所（外部マップ表示に使用） */
  address?: string;
  /** 対象顧客No（周辺検索の起点） */
  customerno?: number;
  /** 顧客を選択確定したとき（参考: selectCustomer のコールバック）。
   *  指定時は選択情報バーに「この顧客を開く」ボタンを表示する。 */
  onSelectCustomer?: (customerno: number) => void;
  /**
   * 復元表示フラグ。true で開いたときは初期化(loadInit)を行わず、前回開いていた
   * ときの地図状態（起点顧客・パン/ズーム・検索条件・選択）をそのまま再表示する。
   * （地図で選んだ顧客の詳細から「戻る」で地図へ戻る用途。）
   */
  restore?: boolean;
  /**
   * 地図の状態コントローラ（useCustomerMap の戻り値）。
   * 顧客選択で詳細へ遷移する際にこのモーダルは一旦アンマウントされる（詳細ページが
   * ローディング中に全画面化するため）。状態をページ側で保持して復元できるよう、
   * hook はページで生成してここへ渡す。
   */
  map: CustomerMapController;
}

/**
 * 顧客地図モーダル
 */
const CustomerMapModal: React.FC<CustomerMapModalProps> = ({ open, onClose, address, customerno, onSelectCustomer, restore = false, map }) => {
  // 検索モーダルの開閉
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  // 検索: 条件種別（querySearchType）
  const [searchType, setSearchType] = useState<string>('');
  // 検索: 登録済検索条件（queryNo）
  const [queryNo, setQueryNo] = useState<number>(CONST_NOT_SELECTED);
  // ボトムシートの高さ状態: 'bar'(バーのみ) / 'peek'(小) / 'full'(大) の3段階
  const [sheetState, setSheetState] = useState<'bar' | 'peek' | 'full'>('peek');

  // モーダルを開いたら初期化（参考: execShowModal → init）。
  // ただし復元表示(restore)のときは loadInit せず、保持している前回の地図状態をそのまま使う。
  useEffect(() => {
    if (open && !restore) {
      map.loadInit(customerno ?? null);
    }
    // open/customerno/restore 変化時のみ。map はhook内で安定参照。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerno, restore]);

  // 検索種別の初期値（init 完了後、先頭種別をセット）
  useEffect(() => {
    const keys = Object.keys(map.querySearchTypeList);
    if (keys.length > 0 && !searchType) {
      setSearchType(keys[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.querySearchTypeList]);

  // 選択中の検索種別で絞り込んだクエリー一覧（参考: setQueryNoList）
  const filteredQueries = useMemo(
    () => map.queryList.filter((q) => q.querysearchtype === searchType),
    [map.queryList, searchType]
  );

  // ハンドルのタップで バーのみ → 小 → 大 → バーのみ … と循環
  // TODO: ドラッグ操作でシート高さを追従させる（現状はタップで切替のみ）
  const toggleSheet = () => {
    setSheetState((s) => (s === 'bar' ? 'peek' : s === 'peek' ? 'full' : 'bar'));
  };

  // 検索実行（参考: selectQuery）
  const handleSearch = () => {
    map.selectQuery(queryNo === CONST_NOT_SELECTED ? null : queryNo);
    setSearchModalOpen(false);
  };

  // 検索条件クリア
  const handleSearchClear = () => {
    const keys = Object.keys(map.querySearchTypeList);
    setSearchType(keys[0] ?? '');
    setQueryNo(CONST_NOT_SELECTED);
  };

  // Googleマップで見る（外部のGoogleマップを開く）。
  // 対象は常に「カルテの顧客（起点顧客）」。選択中のピンには依存しない。
  const handleOpenGoogleMap = () => {
    const h = map.home;
    let query: string | null = null;
    if (h?.latitude != null && h?.longitude != null) {
      query = `${h.latitude},${h.longitude}`;
    } else if (h?.address) {
      query = h.address;
    } else if (address) {
      query = address;
    }
    if (!query) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const pageSize = map.listViewCount || 20;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      maskClosable={false}
      zIndex={MODAL_Z.L1}
      width="100vw"
      style={{ top: 0, maxWidth: '100vw', margin: 0, paddingBottom: 0 }}
      styles={{
        content: { padding: 0, height: '100dvh', borderRadius: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
        header: { margin: 0, padding: '6px 10px', flex: '0 0 auto' },
        body: { padding: 0, flex: '1 1 auto', minHeight: 0 },
      }}
      className="customer-map-modal"
      title={
        <div className="cmm-header">
          <span className="cmm-header-title">周辺顧客</span>
          <div className="cmm-header-actions">
            <Button
              size="small"
              icon={<SearchOutlined />}
              onClick={() => setSearchModalOpen(true)}
            >
              検索
            </Button>
            <Button
              size="small"
              icon={<EnvironmentOutlined />}
              onClick={handleOpenGoogleMap}
            >
              マップで開く
            </Button>
          </div>
        </div>
      }
    >
      <div className="cmm-root">
        {/* ===== 地図エリア（全面） ===== */}
        <div className="cmm-map">
          {map.config ? (
            <MapCanvas
              config={map.config}
              areaList={map.areaList}
              selected={map.selected}
              onBoundsChange={map.searchByBounds}
              onSelectCustomer={map.selectCustomer}
              resetToken={map.mapResetToken}
              active={open}
              initialView={restore ? map.peekView() : null}
              onViewChange={map.rememberView}
            />
          ) : (
            // 地図設定取得前（または取得失敗時）のプレースホルダー
            <div className="cmm-map-placeholder">
              {map.loading ? (
                <Spin />
              ) : (
                <>
                  <EnvironmentFilled className="cmm-pin cmm-pin--self" />
                  <Text type="secondary" className="cmm-map-note">
                    {map.error || '地図を読み込めませんでした'}
                    {address ? <><br />{address}</> : null}
                  </Text>
                </>
              )}
            </div>
          )}
        </div>

        {/* ===== ボトムシート: 周辺顧客リスト ===== */}
        <div className={`cmm-sheet cmm-sheet--${sheetState}`}>
          {/* ハンドル: タップで バーのみ→小→大 を循環 */}
          <div className="cmm-sheet-handle" onClick={toggleSheet}>
            <Button
              type="text"
              size="small"
              icon={sheetState === 'full' ? <DownOutlined /> : <UpOutlined />}
              className="cmm-sheet-toggle"
            />
          </div>

          {/* 選択顧客情報（参考: フッターの No./TEL/携帯/住所 + 前へ/次へ） */}
          {map.selected && (() => {
            const sel = map.selected;
            return (
            <div className="cmm-selected">
              <div className="cmm-selected-main">
                <div className="cmm-selected-names">
                  <span className="cmm-selected-no">No.{sel.customerno}</span>
                  {sel.companyname && <span className="cmm-selected-company">{sel.companyname}</span>}
                  <span className="cmm-selected-name">{sel.customername}</span>
                </div>
                <Space size={4}>
                  <Button
                    size="small"
                    icon={<LeftOutlined />}
                    disabled={!map.canGoPrev}
                    onClick={() => map.goPrev()}
                  />
                  <Button
                    size="small"
                    icon={<RightOutlined />}
                    disabled={!map.canGoNext}
                    onClick={() => map.goNext()}
                  />
                  {onSelectCustomer && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<SelectOutlined />}
                      onClick={() => onSelectCustomer(sel.customerno)}
                    >
                      開く
                    </Button>
                  )}
                </Space>
              </div>
              <div className="cmm-selected-contact">
                {sel.tel && (
                  <a onClick={() => dialPhone(sel.tel!)}>
                    <PhoneOutlined /> {sel.tel}
                  </a>
                )}
                {sel.mobiletel && (
                  <a onClick={() => dialPhone(sel.mobiletel!)}>
                    <MobileOutlined /> {sel.mobiletel}
                  </a>
                )}
              </div>
              {sel.address && (
                <div className="cmm-selected-address">{sel.address}</div>
              )}
            </div>
            );
          })()}

          {/* 件数 + 表示件数（並び替えは要望により非表示） */}
          <div className="cmm-sheet-toolbar">
            <Text strong className="cmm-count">全 {map.customerListCount} 件</Text>
            <Space size={4} wrap>
              {/* 表示件数（参考: map_listViewCount） */}
              <Select
                size="small"
                value={map.listViewCount || undefined}
                onChange={(v) => map.changeListCount(v)}
                options={map.listViewCountMap.map((n) => ({ value: n, label: `${n}件` }))}
                style={{ width: 80 }}
              />
            </Space>
          </div>

          {/* ページング */}
          <div className="cmm-sheet-paging">
            <Pagination
              simple
              size="small"
              current={map.page}
              total={map.customerListCount}
              pageSize={pageSize}
              onChange={(p) => map.changePage(p)}
              itemRender={(_, type, el) =>
                type === 'prev' ? <LeftOutlined /> : type === 'next' ? <RightOutlined /> : el
              }
            />
          </div>

          {/* 顧客カードリスト */}
          <div className="cmm-list">
            {map.customerList.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="該当する顧客はありません"
                style={{ marginTop: 24 }}
              />
            ) : (
              map.customerList.map((c) => {
                const rankname = c.rankname ?? map.resolveRankName(c.rankno);
                // ランク未設定（解決できる rankname が無い）の顧客は顧客一覧と同様に
                // 「ー」＋グレー表示にする。色を rankno から引くとパレット先頭の赤に
                // なってしまい、未設定なのに最上位ランクのように見えるため。
                const hasRank = !!rankname;
                const selected = map.selected?.customerno === c.customerno;
                return (
                  <div
                    key={c.customerno}
                    className={`cmm-card${selected ? ' cmm-card--selected' : ''}`}
                    onClick={() => map.selectCustomer(c.customerno)}
                  >
                    <Tag color={getRankColorByMasterOrder(c.rankno, map.rankList)} className="cmm-card-rank">
                      {hasRank ? rankname : 'ー'}
                    </Tag>
                    <div className="cmm-card-body">
                      {c.updatedate && (
                        <div className="cmm-card-date">{c.updatedate}</div>
                      )}
                      {/* 会社/部署名は2行表示 */}
                      <div className="cmm-card-company">{c.companyname}</div>
                      {c.deptname && <div className="cmm-card-dept">{c.deptname}</div>}
                      <div className="cmm-card-name">{c.customername}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ===== 検索モーダル（登録済検索条件での絞り込み） ===== */}
      <Modal
        open={searchModalOpen}
        title="周辺顧客の検索"
        onCancel={() => setSearchModalOpen(false)}
        zIndex={MODAL_Z.L2}
        mask={false}
        centered
        width={360}
        className="cmm-search-modal"
        footer={[
          <Button key="clear" onClick={handleSearchClear}>
            クリア
          </Button>,
          <Button key="search" type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            検索
          </Button>,
        ]}
      >
        <div className="cmm-search-field">
          <label className="cmm-search-field-label">検索対象</label>
          <Select
            value={searchType || undefined}
            onChange={(v) => {
              setSearchType(v);
              setQueryNo(CONST_NOT_SELECTED);
            }}
            options={Object.entries(map.querySearchTypeList).map(([value, label]) => ({ value, label }))}
            placeholder="種別を選択"
            className="cmm-search-field-input"
          />
        </div>
        <div className="cmm-search-field">
          <label className="cmm-search-field-label">登録済検索条件</label>
          <Select
            value={queryNo === CONST_NOT_SELECTED ? undefined : queryNo}
            onChange={(v) => setQueryNo(v)}
            options={filteredQueries.map((q) => ({ value: q.queryno, label: q.queryname }))}
            placeholder="検索条件を選択"
            allowClear
            onClear={() => setQueryNo(CONST_NOT_SELECTED)}
            className="cmm-search-field-input"
            notFoundContent="条件がありません"
          />
        </div>
      </Modal>
    </Modal>
  );
};

export default CustomerMapModal;
